import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  getActiveSchoolYear,
  getSchoolActiveYear,
  getSchoolYears,
  knownSchoolYears,
  setConsultedSchoolYear,
} from "@/services/schoolService";
import type { SchoolYear } from "@/types";

interface SchoolYearValue {
  /** Années de l'école, de la plus récente à la plus ancienne. */
  years: SchoolYear[];
  /** Année consultée (celle des pages). */
  consulted: string;
  /** Année active de l'école. */
  active: string;
  /** Consulter une autre année (null : revenir à l'année active). */
  consult: (label: string | null) => void;
  /** Relire les années (après création, modification ou activation). */
  reload: () => Promise<void>;
  /** Change à chaque changement d'année : les pages sont rechargées. */
  version: number;
}

const SchoolYearContext = createContext<SchoolYearValue | null>(null);

/** Année scolaire consultée : choisie dans l'en-tête, elle s'applique à toutes les pages. */
export function SchoolYearProvider({ children }: { children: ReactNode }) {
  const [years, setYears] = useState<SchoolYear[]>(knownSchoolYears());
  const [version, setVersion] = useState(0);

  const reload = useCallback(async () => {
    try {
      setYears(await getSchoolYears());
    } catch {
      setYears(knownSchoolYears());
    }
    setVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    if (!knownSchoolYears().length) void reload();
  }, [reload]);

  const consult = useCallback((label: string | null) => {
    setConsultedSchoolYear(label);
    setVersion((v) => v + 1);
  }, []);

  const value = useMemo(
    () => ({ years, consulted: getActiveSchoolYear(), active: getSchoolActiveYear(), consult, reload, version }),
    [years, consult, reload, version],
  );
  return <SchoolYearContext.Provider value={value}>{children}</SchoolYearContext.Provider>;
}

// Hook du contexte, volontairement à côté de son fournisseur (seul le rechargement à chaud en pâtit).
// eslint-disable-next-line react-refresh/only-export-components
export function useSchoolYear(): SchoolYearValue {
  const value = useContext(SchoolYearContext);
  if (!value) throw new Error("useSchoolYear doit être utilisé dans SchoolYearProvider");
  return value;
}
